import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_PALACE =
  "https://customer-assets.emergentagent.com/job_bespoke-events-3/artifacts/fvgw56fc_DSC05097.jpg";

const Portfolio = () => {
  const [films, setFilms] = useState([]);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setFilms(res.data?.items || []))
      .catch(() => setFilms([]));
  }, []);

  if (films.length === 0) return null;

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-24 md:py-40 px-6 md:px-12 lg:px-24"
    >
      <div className="grid lg:grid-cols-12 gap-10 mb-20">
        <div className="lg:col-span-5 reveal">
          <div className="overline mb-6">Weddings</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
            Some weddings,
            <br />
            <span className="italic text-[#B8935C]">a thousand</span> stories.
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 lg:pt-6 reveal">
          <p className="text-[#F0EBE1]/70 text-base sm:text-lg leading-relaxed font-light max-w-lg">
            An editorial glimpse into recent celebrations — from rose-petal
            palace entries in Jaipur to midnight-blue reception fairytales in
            Mumbai. Each film, a love story scored in light.
          </p>
        </div>
      </div>

      <div className="space-y-28 md:space-y-40">
        {films.map((film, fi) => (
          <article
            key={film.id || film.couple}
            data-testid={`film-${fi}`}
            className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center reveal"
          >
            <div
              className={`lg:col-span-5 ${
                fi % 2 === 1 ? "lg:order-2 lg:col-start-8" : ""
              }`}
            >
              <div className="overline mb-5">
                Film No. {String(fi + 1).padStart(2, "0")}
                {film.year ? ` · ${film.year}` : ""}
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight">
                {film.couple?.includes(" & ") ? (
                  <>
                    {film.couple.split(" & ")[0]}
                    <span className="italic text-[#B8935C]"> & </span>
                    {film.couple.split(" & ")[1]}
                  </>
                ) : (
                  film.couple
                )}
              </h3>
              {film.location && (
                <div className="mt-4 text-[#B8935C]/85 text-sm tracking-[0.18em] uppercase">
                  {film.location}
                </div>
              )}
              {film.summary && (
                <p className="mt-8 text-[#F0EBE1]/70 text-base leading-relaxed font-light">
                  {film.summary}
                </p>
              )}
              {film.date && (
                <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 border border-[#B8935C]/40 text-[#B8935C] text-[11px] uppercase tracking-[0.28em]">
                  {film.date}
                </div>
              )}
              {(film.palette || (film.images && film.images.length > 0)) && (
                <div className="mt-10 pt-6 border-t border-[#F0EBE1]/10 grid grid-cols-2 gap-4">
                  {film.palette && (
                    <div>
                      <div className="overline text-[10px] mb-2">Palette</div>
                      <div className="font-serif text-base text-[#F0EBE1]/85 italic">
                        {film.palette}
                      </div>
                    </div>
                  )}
                  {film.images && film.images.length > 0 && (
                    <div>
                      <div className="overline text-[10px] mb-2">Frames</div>
                      <div className="font-serif text-base text-[#F0EBE1]/85">
                        {String(film.images.length).padStart(2, "0")} · curated
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              className={`lg:col-span-7 ${
                fi % 2 === 1 ? "lg:order-1 lg:col-start-1" : ""
              }`}
            >
              {film.images && film.images.length > 0 ? (
                <div
                  className={`grid gap-4 md:gap-5 ${
                    film.images.length > 1
                      ? "grid-cols-1 md:grid-cols-5"
                      : "grid-cols-1"
                  }`}
                >
                  {film.images.map((img, ii) => {
                    const isTall = img.span === "tall";
                    const colClass =
                      film.images.length === 1
                        ? "md:col-span-5 aspect-[16/10]"
                        : isTall
                        ? "md:col-span-2 aspect-[3/4]"
                        : "md:col-span-3 aspect-[4/5] md:aspect-[5/6]";
                    return (
                      <div
                        key={img.id || ii}
                        data-testid={`film-${fi}-image-${ii}`}
                        onMouseEnter={() => setHovered(`${fi}-${ii}`)}
                        onMouseLeave={() => setHovered(null)}
                        className={`group relative overflow-hidden ${colClass}`}
                      >
                        <img
                          src={img.url}
                          alt={`${film.couple} · ${img.label || "wedding film"} · luxury wedding by Bon Evento`}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK_PALACE;
                          }}
                          className="w-full h-full object-cover transition-transform duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                        />
                        <div
                          className={`absolute inset-0 transition-opacity duration-700 ${
                            hovered === `${fi}-${ii}`
                              ? "bg-[#080B12]/35"
                              : "bg-[#080B12]/10"
                          }`}
                        />
                        <div className="absolute inset-0 ring-1 ring-inset ring-[#F0EBE1]/0 group-hover:ring-[#B8935C]/30 transition-all duration-500" />
                        {img.label && (
                          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 translate-y-2 group-hover:translate-y-0 transition-transform duration-700 opacity-0 group-hover:opacity-100">
                            <div className="overline text-[10px] mb-1">
                              {film.couple}
                            </div>
                            <div className="font-serif text-lg md:text-xl text-[#F0EBE1] italic">
                              {img.label}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <div className="mt-24 text-center reveal">
        <a
          href="#contact"
          data-testid="portfolio-cta"
          className="inline-flex items-center gap-3 px-9 py-4 border border-[#B8935C] text-[#B8935C] text-xs uppercase tracking-[0.28em] hover:bg-[#B8935C] hover:text-[#080B12] transition-all duration-500"
        >
          Begin Your Story
        </a>
      </div>
    </section>
  );
};

export default Portfolio;
